import {
  IntegrationCatalogEntry,
  JsonSchemaObject,
} from "../../types/integrationCatalog";

// Fields ending in `_secret_reference` are NAMES of Key Vault secrets, not
// secret values themselves. The runtime resolver reads them from
// `integration.config[<name>]` to look up the actual KV-stored secret. They
// must therefore land in the saved `config` payload, not be stripped and sent
// via `secret_key`.
//
// Other entries in `secret_fields` (e.g. `client_secret`, `api_key`,
// `password`) are the actual secret value the user pastes; the BE encrypts
// them and writes the resulting reference to `integration.secret_reference`.
export const isSecretReferenceField = (name: string): boolean =>
  name.endsWith("_secret_reference");

export const withoutSecretFields = (
  values: Record<string, any>,
  secretFields: string[],
) => {
  const next = { ...values };
  secretFields.forEach((field) => {
    if (isSecretReferenceField(field)) return;
    delete next[field];
  });
  return next;
};

export const firstSecretValue = (
  values: Record<string, any>,
  secretFields: string[],
) => {
  const field = secretFields.find(
    (name) => !isSecretReferenceField(name) && values[name],
  );
  return field ? String(values[field]) : undefined;
};

const cloneSchema = (
  schema?: JsonSchemaObject | null,
): JsonSchemaObject | null => {
  if (!schema) return null;
  return JSON.parse(JSON.stringify(schema));
};

export const omitSchemaProperties = (
  schema: JsonSchemaObject | null,
  omittedKeys: string[],
) => {
  if (!schema) return null;
  const next = cloneSchema(schema);
  if (!next?.properties) return next;
  omittedKeys.forEach((key) => {
    delete next.properties?.[key];
  });
  if (Array.isArray(next.required)) {
    next.required = next.required.filter((key) => !omittedKeys.includes(key));
  }
  return next;
};

export const userCredentialFieldsForSchema = (
  schema?: JsonSchemaObject | null,
) => {
  if (!schema?.properties) return [];
  return Object.entries(schema.properties)
    .filter(([, property]) => property.credential_class === "user")
    .map(([key]) => key);
};

export const omitUserCredentialSchemaProperties = (
  schema: JsonSchemaObject | null,
) => omitSchemaProperties(schema, userCredentialFieldsForSchema(schema));

export const schemaForEntry = (entry?: IntegrationCatalogEntry | null) =>
  entry?.config_schema || entry?.metadata_json?.config_schema || null;

export const secretFieldsForCatalogEntry = (
  entry?: IntegrationCatalogEntry | null,
) => {
  const fields = [
    ...(entry?.metadata_json?.secret_fields || []),
    ...(entry?.secret_fields || []),
  ];
  return Array.from(new Set(fields));
};

export const isLegacyOAuthAuthorizationCodeEntry = (
  entry?: IntegrationCatalogEntry | null,
) =>
  entry?.auth_model === "oauth_authorization_code" &&
  (entry.key === "slack" ||
    entry.metadata_json?.legacy_oauth_authorization_code === true);

export const isGenericOAuthAuthorizationCodeEntry = (
  entry?: IntegrationCatalogEntry | null,
) =>
  entry?.auth_model === "oauth_authorization_code" &&
  !isLegacyOAuthAuthorizationCodeEntry(entry);

export const substrateKeysForEntry = (
  entry: IntegrationCatalogEntry,
): string[] => {
  switch (entry.key) {
    case "sharepoint":
      return ["site_urls"];
    case "email":
      return ["default_sender_address", "sender_addresses", "senders"];
    case "url_crawler":
      return ["max_depth", "max_domains", "allowed_domains"];
    default:
      return [];
  }
};

export const hasSubstrateFields = (entry: IntegrationCatalogEntry) => {
  const properties = schemaForEntry(entry)?.properties;
  return substrateKeysForEntry(entry).some(
    (key) => properties?.[key] !== undefined,
  );
};

export const credentialsSchemaForEntry = (entry: IntegrationCatalogEntry) =>
  omitSchemaProperties(schemaForEntry(entry), substrateKeysForEntry(entry));

export const hasSchemaFields = (schema?: JsonSchemaObject | null) =>
  Boolean(
    schema && schema.properties && Object.keys(schema.properties).length > 0,
  );
